import styled from '@emotion/styled';
import {
  spacing,
  breakpoints,
  palette,
  fontSize,
  Typography,
  Button,
} from '@playdapp/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from 'react-query';

import { chainIdToMainNetworkName } from 'lib/network';
import useTokenCurrency from 'hooks/useTokenCurrency';
import useOpenControl from 'hooks/useOpenControl';
import useListingCancel from 'hooks/useLIstingCancel';
import useCheckNetwork from 'hooks/useCheckNetwork';
import { getFeeValue } from 'api/order';
import { getMyOffers } from 'api/user';
import { clearTxStatus, manageAcceptRequest } from 'store/marketplace';
import {
  cancelBuyOrder as solanaCancelBuyOrder,
  clearTxStatus as solanaClearTxStatus,
  fulfillBuyOffer as solanaFulfillBuyOffer,
} from 'store/marketplaceSolana';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import type { OfferType } from 'types/event';
import type { SelectedOffer } from 'types/offer';

import CustomButton from '@/components/Button';
import SortByDropDown from '@/components/SortByDropDown';
import Table from '@/components/Table';
import CancelModal from '@/components/Modal/CancelModal';
import CancelSuccessModal from '@/components/Modal/CancelSuccessModal';
import TransactionModal from '@/components/Modal/TransactionModal';
import OfferAcceptModal from '@/components/Modal/OfferAcceptModal';
import TransactionResultModal from '@/components/Modal/TransactionResultModal';
import EmptyItem from '@/components/EmptyItem';
import MetaTag from '@/components/MetaTag';
import ErrorContents from '@/components/ErrorContents';

type Event = {
  title: string;
  value: string;
};

const OffersBlock = styled.div`
  position: relative;
`;

const SelectOptionArea = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: ${({ theme }) => theme.rem(48)};
  margin-bottom: ${spacing.xl};

  ${breakpoints.down('md')} {
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;
    height: auto;
    margin-bottom: 1rem;
  }
`;

const OfferEventDrop = styled(SortByDropDown)`
  position: relative;
  align-items: flex-end;
  font-size: ${fontSize.b4};
  font-weight: 600;

  ${breakpoints.down('lg')} {
    display: none;
  }
`;

const MobileOfferEventDrop = styled(SortByDropDown)`
  display: none;

  ${breakpoints.down('lg')} {
    display: block;
    align-items: flex-end;
    width: 100%;
    max-width: ${({ theme }) => theme.rem(200)};
    order: 2;
  }

  ${breakpoints.down('md')} {
    display: block;
    align-items: flex-end;
    width: 100%;
    max-width: 100%;
    margin-right: ${spacing.m};
    order: 1;
  }
`;

const ContentBody = styled.div`
  width: 100%;
  min-height: ${({ theme }) => theme.rem(540)};
`;

const DropDownSortContent = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: ${spacing.xs};
`;

const CollectionLink = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  padding: ${spacing.s} ${spacing.l};
  font-size: ${fontSize.b3};
  font-weight: 600;
  border: none;
  cursor: pointer;

  & > span {
    flex: none;
  }

  &:first-of-type {
    border-top-left-radius: ${spacing.xs};
    border-top-right-radius: ${spacing.xs};
  }

  &:last-of-type {
    border-bottom-left-radius: ${spacing.xs};
    border-bottom-right-radius: ${spacing.xs};
  }

  p {
    margin-left: ${spacing.s};
    padding-top: 1px;
    margin-bottom: -1px;
  }

  &:hover {
    background-color: ${palette.gray300};
  }

  ${breakpoints.down('md')} {
    font-size: ${fontSize.b4};
  }
`;

const CategoryListBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const LoadMore = styled(Button)`
  display: flex;
  align-items: center;
  height: ${spacing.xxl};
  padding: 6px ${spacing.m};
  margin: ${spacing.l} auto;
  border-radius: 6px;

  & + a,
  a + & {
    margin-left: ${spacing.xs};
  }
`;

const EmptyItemBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10rem;

  gap: ${spacing.xl};

  button {
    color: ${palette.white};
    font-weight: 600;
    border-radius: 1.5rem;
  }
`;

const offerEvent = {
  Made: 'Offers made',
  Received: 'Offers received',
};

const MyOffers = () => {
  const dispatch = useAppDispatch();

  const { activeChainId, account } = useAppSelector(({ wallet }) => wallet);
  const { alias } = useAppSelector(({ user }) => user);

  const [transactionModalStatus, setTransactionModalStatus] = useState<
    string | null
  >(null);
  const { txStatus, transactionHash } = useAppSelector(
    ({ marketplace }) => marketplace,
  );
  const { txStatus: solanaTxStatus } = useAppSelector(
    ({ marketplaceSolana }) => marketplaceSolana,
  );

  const { currencyList, convertToUsd } = useTokenCurrency();

  const network = useMemo(
    () => chainIdToMainNetworkName[activeChainId as number],
    [activeChainId],
  );

  const [isClose, setIsClose] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [event, setEvent] = useState<Event>({
    title: 'All Event',
    value: 'All',
  });
  const [selectedOffer, setSelectedOffer] = useState<
    | (SelectedOffer & {
        name: string;
        imgUri: string;
        contractAddress?: string;
      })
    | null
  >(null);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [isAcceptLoading, setIsAcceptLoading] = useState(false);

  const [isTransactionResultModalOpen, handleTransactionResultModalOpen] =
    useOpenControl();
  const [isTransactionModalOpen, handleTransactionModalOpen] = useOpenControl();
  const [isAcceptModalOpen, handleAcceptModalOpen] = useOpenControl();
  const [isCancelModalOpen, handleCancelModalOpen] = useOpenControl();
  const [isCancelSuccessModalOpen, handleCancelSuccessModalOpen] =
    useOpenControl();
  const [transactionResultModalType, setTransactionResultModalType] = useState<
    'error' | 'success'
  >('success');

  const isPostable = useMemo(
    () => (selectedOffer ? !!selectedOffer.name : false),
    [selectedOffer],
  );

  const { isCancelListing, handleCancelListing } = useListingCancel();
  const { checkIsWrongNetwork } = useCheckNetwork();

  const fetchOfferList = async ({ pageParam = 0 }) => {
    const { data } = await getMyOffers({
      network,
      offset: pageParam,
      count: 10,
      type: event.value,
      account: account as string,
    });

    return { ...data, from: pageParam * 10, nextPage: pageParam + 1 };
  };

  const {
    isLoading,
    data: offersData,
    fetchNextPage,
    refetch,
    hasNextPage,
    error,
  } = useInfiniteQuery(['myOffers', event.value, alias], fetchOfferList, {
    getNextPageParam: (lastPage) => {
      if (
        Math.floor((lastPage.count as number) / 10) >= lastPage.nextPage &&
        (lastPage.count as number) > 10
      ) {
        return lastPage.nextPage;
      }
    },
    enabled: !!alias,
    refetchOnWindowFocus: false,
    cacheTime: 0,
  });

  const handleClearTxStatus = () => {
    dispatch(clearTxStatus());
    dispatch(solanaClearTxStatus());
  };

  const toggleAcceptModal =
    (
      isOpen: boolean,
      offer:
        | (SelectedOffer & {
            name: string;
            imgUri: string;
            contractAddress: string;
          })
        | null,
    ) =>
    () => {
      handleAcceptModalOpen(isOpen);
      setSelectedOffer(offer);
    };

  const toggleCancelModal = (
    isOpen: boolean,
    offer: (SelectedOffer & { name: string; imgUri: string }) | null,
  ) => {
    handleCancelModalOpen(isOpen);
    setSelectedOffer(offer);
  };

  const handleEvent = (offerEventParam: Event) => () => {
    if (event.value !== offerEventParam.value) {
      setEvent(offerEventParam);
    }
    setIsClose(true);
  };

  const handleLoadMore = () => {
    fetchNextPage();
  };

  const toggleTransactionResultModal = (isOpen: boolean) => {
    handleTransactionResultModalOpen(isOpen);
    refetch();
    handleClearTxStatus();
  };

  const analyticsToPurchase = useCallback(async () => {
    if (selectedOffer && selectedOffer.contractAddress) {
      const { contractAddress } = selectedOffer;

      try {
        const { data: resFee } = await getFeeValue({
          network,
          contractAddress,
        });

        const usd =
          convertToUsd(selectedOffer.price, network, selectedOffer.currency) ??
          0;

        const fee = resFee.playdapp?.eventFee
          ? resFee?.playdapp?.eventFee
          : resFee?.playdapp?.baseFee;

        window.dataLayer.push({
          event: 'purchase',
          transaction_id:
            transactionHash ??
            `${selectedOffer.tokenId}-${selectedOffer.price}`,
          affiliation: 'PlayDapp Marketplace',
          value: +selectedOffer.price * +fee * (1 / 100) * +usd,
          currency: 'usd',
          fee,
          tax: 0,
          shipping: 0,
          items: {
            item_id: selectedOffer.tokenId,
            item_name: selectedOffer.name,
            affiliation: 'PlayDapp Marketplace',
          },
        });
      } catch (e) {
        // TODO: failure sending data to analytics
        console.error(e);
      }
    }
  }, [network, selectedOffer, transactionHash, convertToUsd]);

  const handleAccept = async () => {
    try {
      if (!account) {
        return;
      }

      if (checkIsWrongNetwork(network)) {
        return;
      }

      if (!selectedOffer) return;

      const { orderId, tokenId, contractAddress, pda } = selectedOffer;

      if (network === 'solana') {
        setIsAcceptLoading(true);
        dispatch(solanaFulfillBuyOffer({ pda: pda || '' }));
        return;
      }

      const params = {
        tokenId: tokenId as string,
        assetId: contractAddress as string,
        accountAddress: account,
        orderId,
      };

      setIsClicked(true);

      await dispatch(manageAcceptRequest(params));
    } catch (err) {
      console.error(err);
    } finally {
      setIsClicked(false);
    }
  };

  const handleCancel = () => {
    if (!selectedOffer) {
      return;
    }

    const { orderId, from, pda } = selectedOffer;

    if (network === 'solana') {
      setIsCancelLoading(true);
      dispatch(solanaCancelBuyOrder({ pda: pda || '' }));
      return;
    }

    handleCancelListing({
      orderId,
      network,
      address: from,
      callback: () => {
        handleCancelSuccessModalOpen(true);
        handleCancelModalOpen(false);
        setSelectedOffer(null);
        refetch();
      },
    });
  };

  useEffect(() => {
    if (!selectedOffer) return;

    switch (txStatus || solanaTxStatus) {
      case 'INITIALIZE_PROXY':
      case 'APPROVE_ALL_ASSETS':
      case 'APPROVE_ASSET':
        setTransactionModalStatus(txStatus);
        handleTransactionModalOpen(true);
        handleAcceptModalOpen(false);
        break;
      case 'APPROVE_CURRENCY':
        setTransactionModalStatus(
          `APPROVE_CURRENCY_${selectedOffer.currency.toUpperCase()}`,
        );
        handleTransactionModalOpen(true);
        handleAcceptModalOpen(false);
        break;
      case 'FULFILL_ORDER':
        setTransactionModalStatus('FULFILL_ORDER_OFFER_ACCEPT');
        handleTransactionModalOpen(true);
        handleAcceptModalOpen(false);
        break;
      case 'TX_SUCCESS':
        handleTransactionResultModalOpen(true);
        setTransactionResultModalType('success');
        handleTransactionModalOpen(false);
        setTransactionModalStatus('');
        analyticsToPurchase();
        setIsCancelLoading(false);
        setIsAcceptLoading(false);

        if (solanaTxStatus) {
          toggleCancelModal(false, null);
          toggleAcceptModal(false, null);
        }

        break;
      case 'TX_FAILED':
        handleTransactionResultModalOpen(true);
        setTransactionResultModalType('error');
        handleTransactionModalOpen(false);
        setTransactionModalStatus('');
        setIsCancelLoading(false);
        setIsAcceptLoading(false);

        if (solanaTxStatus) {
          toggleCancelModal(false, null);
          toggleAcceptModal(false, null);
        }
        break;
      default:
        break;
    }
  }, [
    txStatus,
    solanaTxStatus,
    selectedOffer,
    handleAcceptModalOpen,
    handleTransactionModalOpen,
    handleTransactionResultModalOpen,
    analyticsToPurchase,
  ]);

  useEffect(() => {
    if (isClose) {
      setIsClose(() => false);
    }
  }, [isClose]);

  useEffect(() => {
    refetch();
  }, [alias, refetch]);

  useEffect(
    () => () => {
      handleClearTxStatus();
    },
    [dispatch],
  );

  return (
    <>
      <MetaTag title="My Offers | PlayDapp" />
      <OffersBlock>
        <SelectOptionArea>
          <OfferEventDrop
            title={event.title}
            width="266"
            height="48"
            top="48"
            isBorder
            isDropBox
            floatingContent
            isClose={isClose}
          >
            <DropDownSortContent>
              <CategoryListBlock>
                <CollectionLink
                  onClick={handleEvent({ title: 'All Event', value: 'all' })}
                >
                  All Event
                </CollectionLink>
                <CollectionLink
                  onClick={handleEvent({
                    title: 'Offers received',
                    value: 'Received',
                  })}
                >
                  Offers received
                </CollectionLink>
                <CollectionLink
                  onClick={handleEvent({
                    title: 'Offers made',
                    value: 'Made',
                  })}
                >
                  Offers made
                </CollectionLink>
              </CategoryListBlock>
            </DropDownSortContent>
          </OfferEventDrop>
          <MobileOfferEventDrop
            title={event.title}
            width="100%"
            height="36"
            top="40"
            isBorder
            isDropBox
            floatingContent
            isMobile
          >
            <DropDownSortContent>
              <CategoryListBlock>
                <CollectionLink
                  onClick={handleEvent({ title: 'All Event', value: 'all' })}
                >
                  All Event
                </CollectionLink>
                <CollectionLink
                  onClick={handleEvent({
                    title: 'Offers received',
                    value: 'offersReceived',
                  })}
                >
                  Offers received
                </CollectionLink>
                <CollectionLink
                  onClick={handleEvent({
                    title: 'Offers made',
                    value: 'offersMade',
                  })}
                >
                  Offers made
                </CollectionLink>
              </CategoryListBlock>
            </DropDownSortContent>
          </MobileOfferEventDrop>
        </SelectOptionArea>
        <ContentBody>
          {!error &&
            offersData?.pages &&
            offersData?.pages.length > 0 &&
            offersData?.pages[0].offers?.length === 0 && (
              <EmptyItemBlock>
                <EmptyItem text="No Offers yet" />
                <CustomButton to={`/nfts/${network}/all`}>
                  Find assets you can bid on
                </CustomButton>
              </EmptyItemBlock>
            )}
          {!isLoading && error && (
            <EmptyItemBlock>
              <ErrorContents />
            </EmptyItemBlock>
          )}
          {offersData?.pages[0].offers?.length !== 0 && (
            <>
              <Table
                title="Offers"
                headers={[
                  'Event',
                  'Token ID',
                  'NFT Name',
                  'Offer Price',
                  'Date',
                  'Status',
                ]}
              >
                {offersData?.pages.map((offerData) => {
                  const { offers } = offerData;
                  return offers?.map((info, idx) => (
                    <Table.MyOfferItem
                      key={`my-offer-item-${info.orderId}-${idx}`}
                      name={info.tokenName}
                      event={
                        offerEvent[
                          info.type as 'Made' | 'Received'
                        ] as OfferType
                      }
                      tokenId={info.tokenId}
                      tokenAddress={info.assetAddress}
                      price={info.price}
                      currency={info.currencySymbol?.toLowerCase()}
                      toUsdPrice={convertToUsd(
                        info.price,
                        network,
                        info.currencySymbol.toUpperCase(),
                      )}
                      expirationTime={info.statusTime}
                      isOwner={info.type === 'Received'}
                      isBidder={info.type === 'Made'}
                      status={info.status}
                      network={network}
                      image={info.tokenImgUrl}
                      handleAccept={toggleAcceptModal(true, {
                        orderId: info.orderId,
                        name: info.tokenName,
                        imgUri: info.tokenImgUrl,
                        tokenId: info.tokenId,
                        price: info.price,
                        from: info.maker,
                        currency: info.currencySymbol,
                        contractAddress: info.assetAddress,
                        pda: info.pda,
                      })}
                      handleCancel={() =>
                        toggleCancelModal(true, {
                          orderId: info.orderId,
                          name: info.tokenName,
                          imgUri: info.tokenImgUrl,
                          price: info.price,
                          from: info.maker,
                          currency: info.currencySymbol,
                          pda: info.pda,
                        })
                      }
                    />
                  ));
                })}
              </Table>

              {hasNextPage && (
                <LoadMore
                  onClick={handleLoadMore}
                  size="xs"
                  variant="outline"
                  color="secondary"
                >
                  <Typography color="dgray600" type="p5">
                    Load More +
                  </Typography>
                </LoadMore>
              )}
            </>
          )}
        </ContentBody>

        {!!offersData && currencyList && selectedOffer && (
          <OfferAcceptModal
            isOpen={isAcceptModalOpen}
            isClicked={isClicked}
            isLoading={isAcceptLoading}
            imgUrl={selectedOffer.imgUri}
            name={selectedOffer.name ?? ''}
            toUsdPrice={convertToUsd(
              selectedOffer?.price,
              network,
              selectedOffer?.currency.toUpperCase(),
            )}
            price={selectedOffer?.price}
            currency={selectedOffer?.currency.toUpperCase()}
            network={network}
            contractAddress={selectedOffer.contractAddress as string}
            isPostable={isPostable}
            handleOpen={toggleAcceptModal(false, null)}
            handleAccept={handleAccept}
          />
        )}
        {!!offersData && selectedOffer && (
          <CancelModal
            isOpen={isCancelModalOpen}
            isCancelListing={isCancelListing || isCancelLoading}
            title={selectedOffer.name}
            type="offer"
            images={[selectedOffer.imgUri]}
            handleOpen={handleCancelModalOpen}
            handleClose={() => toggleCancelModal(false, null)}
            handleCancel={handleCancel}
          />
        )}
        <CancelSuccessModal
          isOpen={isCancelSuccessModalOpen}
          network={network}
          handleOpen={handleCancelSuccessModalOpen}
          type=""
        />
        {selectedOffer && (
          <TransactionModal
            isOpen={isTransactionModalOpen}
            handleOpen={handleTransactionModalOpen}
            status={transactionModalStatus}
            network={network}
            shouldCloseOnOverlayClick={false}
            stepTypeSubFix="AcceptOffer"
            currency={selectedOffer?.currency?.toLowerCase()}
            txHash={transactionHash}
          />
        )}
        <TransactionResultModal
          isOpen={isTransactionResultModalOpen}
          handleOpen={() => toggleTransactionResultModal(false)}
          type={transactionResultModalType}
          network={network}
          txHash={transactionHash}
        />
      </OffersBlock>
    </>
  );
};

export default MyOffers;
